// CobrosB2B.jsx — Conciliación de facturas B2B contra extracto bancario.
//
// B2B paga por TRANSFERENCIA BANCARIA a cuenta Limpack (decisión 01-jun-2026).
// No hay reporte automático del procesador, así que Roberto/Nico revisan extracto
// BROU/Itaú y marcan cobrado manualmente.
//
// Lista facturas con empresa_id o suscripcion_empresa_id (B2B). Permite filtrar
// por estado de cobro, empresa y mes. Modal para marcar cobrado con fecha + referencia.

import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const ESTADO_COBRO_COLOR = {
  pendiente: { bg: '#FEF3C7', fg: '#92400E', label: '⏳ Pendiente' },
  cobrado:   { bg: '#DCFCE7', fg: '#16A34A', label: '✓ Cobrado' },
  vencido:   { bg: '#FEE2E2', fg: '#DC2626', label: '⚠ Vencido' },
}

const TIPO_CFE_LABEL = {
  e_ticket: 'eTicket',
  e_factura: 'eFactura',
  e_nota_credito: 'NC',
  e_nota_debito: 'ND',
  otro: 'Otro',
}

function fmtFecha(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function fmtMoneda(n) {
  return `$${Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })}`
}
function diasAVencimiento(vencimiento) {
  if (!vencimiento) return null
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  const v = new Date(vencimiento); v.setHours(0, 0, 0, 0)
  return Math.round((v - hoy) / (1000 * 60 * 60 * 24))
}

export default function CobrosB2B() {
  const [facturas, setFacturas] = useState([])
  const [empresasMap, setEmpresasMap] = useState({})
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [mensaje, setMensaje] = useState(null)

  // Filtros
  const [estadoCobro, setEstadoCobro] = useState('todos')
  const [empresaFiltro, setEmpresaFiltro] = useState('todas')
  const [busqueda, setBusqueda] = useState('')
  const [modalCobro, setModalCobro] = useState(null)
  // Toggle "Ver pruebas" - persistido en localStorage, default OFF
  const [mostrarPruebas, setMostrarPruebas] = useState(() => {
    try { return localStorage.getItem('limpack_cobros_b2b_ver_pruebas') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('limpack_cobros_b2b_ver_pruebas', mostrarPruebas ? '1' : '0') } catch {}
  }, [mostrarPruebas])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true); setError(null)
    try {
      const [{ data: facs, error: errF }, { data: emps, error: errE }] = await Promise.all([
        supabase.from('facturas_emitidas')
          .select(`
            id, tipo_cfe, serie, numero, fecha_emision, vencimiento,
            receptor_razon_social, receptor_rut, monto_total, moneda,
            estado, estado_dgi, sync_origen,
            empresa_id, suscripcion_empresa_id, pedido_id,
            cobro_b2b_estado, cobro_b2b_fecha, cobro_b2b_referencia, cobro_b2b_notas,
            es_prueba,
            notas, created_at
          `)
          .or('empresa_id.not.is.null,suscripcion_empresa_id.not.is.null')
          .neq('estado', 'anulada')
          .order('fecha_emision', { ascending: false })
          .limit(500),
        supabase.from('empresas').select('id, razon_social, rut'),
      ])
      if (errF) throw errF
      if (errE) throw errE
      setFacturas(facs || [])
      setEmpresasMap(Object.fromEntries((emps || []).map(e => [e.id, e])))
    } catch (e) {
      setError(e.message || String(e))
    }
    setCargando(false)
  }

  // Recalcular vencido en frontend (por si el cron no corre todavía)
  const facturasConEstado = useMemo(() => facturas.map(f => {
    let estadoCalc = f.cobro_b2b_estado || 'pendiente'
    if (estadoCalc === 'pendiente' && f.vencimiento) {
      const dias = diasAVencimiento(f.vencimiento)
      if (dias !== null && dias < 0) estadoCalc = 'vencido'
    }
    return { ...f, _estadoCalc: estadoCalc, _diasVenc: diasAVencimiento(f.vencimiento) }
  }), [facturas])

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return facturasConEstado.filter(f => {
      if (!mostrarPruebas && f.es_prueba) return false
      if (estadoCobro !== 'todos' && f._estadoCalc !== estadoCobro) return false
      if (empresaFiltro !== 'todas' && f.empresa_id !== empresaFiltro) return false
      if (q) {
        const emp = empresasMap[f.empresa_id]
        const hay = [
          f.serie, f.numero, f.receptor_razon_social, f.receptor_rut,
          emp?.razon_social, f.cobro_b2b_referencia,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [facturasConEstado, estadoCobro, empresaFiltro, busqueda, empresasMap, mostrarPruebas])

  const totalPruebas = useMemo(() => facturasConEstado.filter(f => f.es_prueba).length, [facturasConEstado])

  const kpis = useMemo(() => {
    // KPIs SIEMPRE excluyen es_prueba (números reales de negocio)
    const reales = facturasConEstado.filter(f => !f.es_prueba)
    const total = reales.reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const pendientes = reales.filter(f => f._estadoCalc === 'pendiente')
    const vencidas = reales.filter(f => f._estadoCalc === 'vencido')
    const cobradas = reales.filter(f => f._estadoCalc === 'cobrado')
    return {
      total,
      cantPendientes: pendientes.length, montoPendientes: pendientes.reduce((s, f) => s + Number(f.monto_total || 0), 0),
      cantVencidas: vencidas.length, montoVencidas: vencidas.reduce((s, f) => s + Number(f.monto_total || 0), 0),
      cantCobradas: cobradas.length, montoCobradas: cobradas.reduce((s, f) => s + Number(f.monto_total || 0), 0),
    }
  }, [facturasConEstado])

  const empresasPresentes = useMemo(() => {
    const ids = new Set(facturasConEstado.map(f => f.empresa_id).filter(Boolean))
    return Array.from(ids).map(id => empresasMap[id]).filter(Boolean)
  }, [facturasConEstado, empresasMap])

  async function marcarCobrado({ facturaId, fecha, referencia, notas }) {
    setMensaje(null)
    try {
      const { error: e } = await supabase.from('facturas_emitidas').update({
        cobro_b2b_estado: 'cobrado',
        cobro_b2b_fecha: fecha,
        cobro_b2b_referencia: referencia,
        cobro_b2b_notas: notas,
      }).eq('id', facturaId)
      if (e) throw e
      setMensaje({ tipo: 'ok', texto: '✓ Marcado como cobrado' })
      setModalCobro(null)
      cargar()
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + (e.message || String(e)) })
    }
  }

  async function deshacerCobro(facturaId) {
    if (!confirm('¿Volver a marcar como pendiente? Se borra fecha + referencia.')) return
    try {
      const { error: e } = await supabase.from('facturas_emitidas').update({
        cobro_b2b_estado: 'pendiente',
        cobro_b2b_fecha: null,
        cobro_b2b_referencia: null,
      }).eq('id', facturaId)
      if (e) throw e
      setMensaje({ tipo: 'ok', texto: '↩ Vuelto a pendiente' })
      cargar()
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + (e.message || String(e)) })
    }
  }

  return (
    <AdminLayout>
      <div className="cb">
        <header className="cb-header">
          <div>
            <h1>Cobros B2B vs extracto bancario</h1>
            <p className="cb-sub">Facturas B2B emitidas. Marcar cobrado cuando llegue la transferencia al banco.</p>
          </div>
          <button className="cb-btn" onClick={cargar} disabled={cargando}>{cargando ? 'Cargando…' : '↻ Refrescar'}</button>
        </header>

        <section className="cb-kpis">
          <Kpi label="Total facturado B2B" value={fmtMoneda(kpis.total)} sub={`${facturasConEstado.length} facturas`} color="navy" />
          <Kpi label="Pendientes" value={fmtMoneda(kpis.montoPendientes)} sub={`${kpis.cantPendientes} facturas`} color={kpis.cantPendientes > 0 ? 'warn' : 'ok'} />
          <Kpi label="Vencidas" value={fmtMoneda(kpis.montoVencidas)} sub={`${kpis.cantVencidas} facturas`} color={kpis.cantVencidas > 0 ? 'danger' : 'ok'} />
          <Kpi label="Cobradas" value={fmtMoneda(kpis.montoCobradas)} sub={`${kpis.cantCobradas} facturas`} color="ok" />
        </section>

        {mensaje && (
          <div className={`cb-msg cb-msg-${mensaje.tipo}`}>
            {mensaje.texto}
            <button className="cb-msg-close" onClick={() => setMensaje(null)}>✕</button>
          </div>
        )}

        <section className="cb-filtros">
          <input type="search" placeholder="Buscar serie, número, RUT, razón social, referencia…"
            value={busqueda} onChange={e => setBusqueda(e.target.value)} className="cb-input cb-input-search" />
          <select value={estadoCobro} onChange={e => setEstadoCobro(e.target.value)} className="cb-select">
            <option value="todos">Estado: todos</option>
            <option value="pendiente">⏳ Pendiente</option>
            <option value="vencido">⚠ Vencido</option>
            <option value="cobrado">✓ Cobrado</option>
          </select>
          <select value={empresaFiltro} onChange={e => setEmpresaFiltro(e.target.value)} className="cb-select">
            <option value="todas">Empresa: todas</option>
            {empresasPresentes.map(e => (
              <option key={e.id} value={e.id}>{e.razon_social}</option>
            ))}
          </select>
          {totalPruebas > 0 && (
            <button
              type="button"
              onClick={() => setMostrarPruebas(v => !v)}
              title={mostrarPruebas ? 'Ocultar facturas B2B marcadas como prueba' : 'Mostrar facturas B2B marcadas como prueba (test/desarrollo)'}
              style={{
                padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: mostrarPruebas ? '#FEF3C7' : '#F1F5F9',
                color: mostrarPruebas ? '#78350F' : '#475569',
                border: `1px solid ${mostrarPruebas ? '#FCD34D' : '#CBD5E1'}`,
                marginLeft: 'auto',
              }}
            >
              🧪 {mostrarPruebas ? `Ocultar pruebas (${totalPruebas})` : `Ver pruebas (${totalPruebas})`}
            </button>
          )}
        </section>

        {error && <div className="cb-error">⚠ {error}</div>}

        {cargando ? (
          <div className="cb-loading">Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="cb-empty">
            <p>Sin facturas B2B que coincidan con los filtros.</p>
          </div>
        ) : (
          <div className="cb-tabla-wrap">
            <table className="cb-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>CFE</th>
                  <th>Empresa</th>
                  <th>Monto</th>
                  <th>Vence</th>
                  <th>Estado</th>
                  <th>Referencia / Cobrado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map(f => {
                  const emp = empresasMap[f.empresa_id]
                  const color = ESTADO_COBRO_COLOR[f._estadoCalc] || ESTADO_COBRO_COLOR.pendiente
                  return (
                    <tr key={f.id} className={`cb-fila cb-fila-${f._estadoCalc}`}>
                      <td>{fmtFecha(f.fecha_emision)}</td>
                      <td>
                        <strong>{f.serie ? `${f.serie}-` : ''}{f.numero || '—'}</strong>
                        <div className="cb-mini">{TIPO_CFE_LABEL[f.tipo_cfe] || f.tipo_cfe}</div>
                      </td>
                      <td>
                        {emp ? (
                          <>
                            <strong>{emp.razon_social}</strong>
                            {emp.rut && <div className="cb-mini">RUT {emp.rut}</div>}
                          </>
                        ) : (
                          <span className="cb-muted">{f.receptor_razon_social || 'Sin empresa'}</span>
                        )}
                      </td>
                      <td className="cb-num"><strong>{fmtMoneda(f.monto_total)}</strong></td>
                      <td>
                        {fmtFecha(f.vencimiento)}
                        {f._diasVenc !== null && f._estadoCalc !== 'cobrado' && (
                          <div className={`cb-mini ${f._diasVenc < 0 ? 'cb-mini-warn' : ''}`}>
                            {f._diasVenc < 0 ? `Vencida hace ${-f._diasVenc} día(s)` : `En ${f._diasVenc} día(s)`}
                          </div>
                        )}
                      </td>
                      <td><span className="cb-badge" style={{ background: color.bg, color: color.fg }}>{color.label}</span></td>
                      <td>
                        {f.cobro_b2b_estado === 'cobrado' ? (
                          <>
                            <div><strong>{fmtFecha(f.cobro_b2b_fecha)}</strong></div>
                            {f.cobro_b2b_referencia && <div className="cb-mini">{f.cobro_b2b_referencia}</div>}
                          </>
                        ) : (
                          <span className="cb-muted">—</span>
                        )}
                      </td>
                      <td>
                        {f._estadoCalc === 'cobrado' ? (
                          <button className="cb-btn-link" onClick={() => deshacerCobro(f.id)} title="Volver a pendiente">↩ Deshacer</button>
                        ) : (
                          <button className="cb-btn-primary" onClick={() => setModalCobro(f)}>✓ Marcar cobrado</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="cb-footer">{filtradas.length} de {facturasConEstado.length} facturas B2B</div>
          </div>
        )}

        {modalCobro && (
          <ModalCobrar factura={modalCobro} empresa={empresasMap[modalCobro.empresa_id]} onCerrar={() => setModalCobro(null)} onConfirmar={marcarCobrado} />
        )}
      </div>
      <style>{css}</style>
    </AdminLayout>
  )
}

function Kpi({ label, value, sub, color = 'navy' }) {
  return (
    <div className={`cb-kpi cb-kpi-${color}`}>
      <div className="cb-kpi-label">{label}</div>
      <div className="cb-kpi-value">{value}</div>
      <div className="cb-kpi-sub">{sub}</div>
    </div>
  )
}

function ModalCobrar({ factura, empresa, onCerrar, onConfirmar }) {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10))
  const [referencia, setReferencia] = useState('')
  const [notas, setNotas] = useState('')
  const [guardando, setGuardando] = useState(false)

  async function submit(e) {
    e.preventDefault()
    if (!fecha) return
    setGuardando(true)
    await onConfirmar({ facturaId: factura.id, fecha, referencia: referencia.trim() || null, notas: notas.trim() || null })
    setGuardando(false)
  }

  return (
    <div className="cb-modal-overlay" onClick={onCerrar}>
      <div className="cb-modal" onClick={e => e.stopPropagation()}>
        <h2>Marcar cobrado</h2>
        <p className="cb-modal-info">
          <strong>{factura.serie}-{factura.numero}</strong> · {empresa?.razon_social || factura.receptor_razon_social || '—'}<br/>
          Monto: <strong>{fmtMoneda(factura.monto_total)}</strong>
        </p>
        <form onSubmit={submit}>
          <label>Fecha del cobro
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} required />
          </label>
          <label>Referencia bancaria (Nº transferencia, banco)
            <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="Ej: BROU TR-12345 / Itaú #98765" />
          </label>
          <label>Notas (opcional)
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" placeholder="Comentario interno" />
          </label>
          <div className="cb-modal-actions">
            <button type="button" className="cb-btn" onClick={onCerrar}>Cancelar</button>
            <button type="submit" className="cb-btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : '✓ Confirmar cobro'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

const css = `
.cb { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: Inter, sans-serif; color: var(--cl-navy, #1A3461); }
.cb-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; gap: 16px; flex-wrap: wrap; }
.cb-header h1 { margin: 0 0 4px; font-size: 24px; }
.cb-sub { color: #64748B; margin: 0; font-size: 14px; }
.cb-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 20px; }
.cb-kpi { background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 12px; padding: 16px; }
.cb-kpi-label { font-size: 12px; color: #64748B; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; }
.cb-kpi-value { font-size: 24px; font-weight: 800; margin: 4px 0; }
.cb-kpi-sub { font-size: 12px; color: #64748B; }
.cb-kpi-navy { border-left: 4px solid #1A3461; }
.cb-kpi-ok { border-left: 4px solid #16A34A; }
.cb-kpi-warn { border-left: 4px solid #F59E0B; }
.cb-kpi-danger { border-left: 4px solid #DC2626; }
.cb-filtros { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
.cb-input, .cb-select { padding: 8px 12px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 8px; font-size: 14px; font-family: inherit; }
.cb-input-search { flex: 1 1 240px; }
.cb-tabla-wrap { background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 12px; overflow-x: auto; }
.cb-tabla { width: 100%; border-collapse: collapse; font-size: 13px; }
.cb-tabla th { background: #F8FAFC; padding: 10px 12px; text-align: left; font-weight: 700; color: var(--cl-navy, #1A3461); border-bottom: 2px solid var(--cl-line, #e2e8f0); white-space: nowrap; }
.cb-tabla td { padding: 12px; border-bottom: 1px solid var(--cl-line, #e2e8f0); vertical-align: top; }
.cb-fila-vencido { background: #FEF2F2; }
.cb-fila-cobrado { background: #F0FDF4; }
.cb-num { text-align: right; }
.cb-mini { font-size: 11px; color: #64748B; }
.cb-mini-warn { color: #DC2626; font-weight: 600; }
.cb-muted { color: #94A3B8; font-style: italic; }
.cb-badge { font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
.cb-btn, .cb-btn-primary, .cb-btn-link { padding: 6px 12px; border: 1.5px solid var(--cl-line, #e2e8f0); background: #fff; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; color: var(--cl-navy, #1A3461); font-family: inherit; }
.cb-btn:hover { border-color: var(--cl-teal, #00A8B5); color: var(--cl-teal, #00A8B5); }
.cb-btn-primary { background: var(--cl-teal, #00A8B5); color: #fff; border-color: var(--cl-teal, #00A8B5); }
.cb-btn-primary:hover { background: #008C97; }
.cb-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
.cb-btn-link { background: transparent; border: none; padding: 4px 8px; }
.cb-btn-link:hover { color: var(--cl-teal, #00A8B5); }
.cb-loading, .cb-empty, .cb-error { background: #fff; padding: 40px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 12px; text-align: center; color: #64748B; }
.cb-error { color: #DC2626; border-color: #FECACA; background: #FEF2F2; }
.cb-msg { padding: 12px 16px; border-radius: 8px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 12px; font-size: 14px; }
.cb-msg-ok { background: #DCFCE7; color: #166534; }
.cb-msg-error { background: #FEE2E2; color: #991B1B; }
.cb-msg-close { background: transparent; border: none; cursor: pointer; font-size: 18px; color: inherit; }
.cb-footer { padding: 12px; border-top: 1.5px solid var(--cl-line, #e2e8f0); font-size: 12px; color: #64748B; }
.cb-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.cb-modal { background: #fff; border-radius: 12px; padding: 24px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; }
.cb-modal h2 { margin: 0 0 12px; font-size: 20px; }
.cb-modal-info { background: #F8FAFC; padding: 12px; border-radius: 8px; font-size: 13px; margin-bottom: 16px; }
.cb-modal label { display: block; margin-bottom: 12px; font-size: 13px; font-weight: 600; }
.cb-modal label input, .cb-modal label textarea { display: block; width: 100%; padding: 8px 10px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 6px; font-size: 14px; margin-top: 4px; font-family: inherit; box-sizing: border-box; }
.cb-modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
`
